import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const IS_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Build a map of templateId (UUID or name) → template name using the catalog */
function buildTemplateNameMap(catalog) {
  const map = {};
  if (!Array.isArray(catalog)) return map;
  catalog.forEach(entry => {
    (entry.templates || []).forEach(t => {
      if (t.id && t.name) map[t.id] = t.name;
    });
  });
  return map;
}

/** Resolve templateId to a template name (handles both UUIDs and plain name strings) */
function resolveTemplateName(templateId, nameMap) {
  if (!templateId) return null;
  if (nameMap[templateId]) return nameMap[templateId];
  if (!IS_UUID.test(templateId)) return templateId; // already a name
  return null;
}

// Primary template colors — background + main accent colors per template
// Includes all visually distinctive non-near-white colors so they appear in the board palette
const TEMPLATE_COLORS = {
  ZmanimWidget: {
    Dark:       ['#0d1b2a', '#8a6d08'],
    Elegant:    ['#1c1c2e', '#9a7209'],
    Parchment:  ['#f5f0e0', '#b8860b', '#3e2723'],
  },
  OmerWidget: {
    Elegant:    ['#0f1e35', '#c9a227'],
    Kabbalah:   ['#1a1a2e', '#c9a227'],
    Parchment:  ['#f5f0e0', '#b8860b'],
  },
  ClockWidget: {
    DarkNavy:   ['#0f1e35'],
  },
  ShabbatWidget: {
    Times:      ['#1e3a5f', '#8a6d08'],
    Card:       ['#3e2723', '#fff8e1', '#8d6e63'],
    Modern:     ['#1e3a5f', '#8a6d08'],
    Parchment:  ['#fdf6e3', '#b8860b', '#3e2723'],
    Royal:      ['#0d1b2a', '#c9a227'],
    Horizontal: ['#1e3a5f', '#9a7209'],
    Classic:    ['#1e3a5f', '#9a7209'],
    Candle:     ['#1a1520', '#d4820a', '#8fa8c8'],
  },
  MoadWidget: {
    Elegant:    ['#162540', '#c9a227'],
  },
  SimchaWidget: {
    Parchment:  ['#fdf6e3', '#b8860b'],
    Classic:    ['#1a3352', '#c9a227'],
  },
};

function isValidHex(color) {
  return typeof color === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(color);
}

/**
 * Returns a deduplicated array of hex color strings currently used on the board.
 * Includes: dashboard bg, widget backgrounds, widget-specific color props, template colors.
 * Memoized — only recomputes when widgets or settings change.
 */
export function useBoardColors() {
  const widgets  = useSelector(s => s.dashboard?.dashboard?.current?.widgets);
  const settings = useSelector(s => s.dashboard?.dashboard?.current?.settings);
  const catalog  = useSelector(s => s.app?.catalog);

  return useMemo(() => {
    const nameMap = buildTemplateNameMap(catalog);
    const seen = new Set();
    const add = (c) => { if (isValidHex(c)) seen.add(c.toLowerCase()); };

    // Dashboard-level colors
    add(settings?.bgColor);
    add(settings?.widgetShadowColor);

    (widgets || []).forEach(w => {
      // Widget background (user-set)
      add(w.widgetSettings?.bgColor);

      const p = w.props || {};
      if (w.componentKey === 'ParnasWidget' && p.tileColors) {
        add(p.tileColors.daily);
        add(p.tileColors.weekly);
        add(p.tileColors.monthly);
      }
      if (w.componentKey === 'ZmanimWidget') add(p.shabbatColor);
      if (w.componentKey === 'ClockWidget') { add(p.textColor); add(p.accentColor); }
      if (w.componentKey === 'OmerWidget') add(p.bgColor);

      // Template primary colors — resolve UUID → name first
      const tName = resolveTemplateName(w.templateId, nameMap);
      TEMPLATE_COLORS[w.componentKey]?.[tName]?.forEach(add);
    });

    return [...seen];
  }, [widgets, settings, catalog]);
}
